import { useState } from 'react';
import { destinationHighlights, type DestinationHighlight } from '../../../mocks/destination-highlights';

interface DestinationHighlightsProps {
  selectedCountry: string;
}

function HighlightCard({ highlight }: { highlight: DestinationHighlight }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer">
      <div className="relative h-64 w-full overflow-hidden bg-gray-100">
        {!imgError ? (
          <img
            src={highlight.image}
            alt={highlight.title}
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#f5f0e8] to-[#e8dcc8]">
            <div className="w-16 h-16 flex items-center justify-center">
              <i className={`${highlight.icon} text-[#C9A961] text-5xl`}></i>
            </div>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-6 left-6 right-6">
          <div className="w-12 h-12 bg-[#C9A961] rounded-full flex items-center justify-center mb-3">
            <i className={`${highlight.icon} text-white text-2xl`}></i>
          </div>
          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
            {highlight.title}
          </h3>
        </div>
      </div>
      <div className="p-6">
        <p className="text-gray-600 leading-relaxed">{highlight.description}</p>
      </div>
    </div>
  );
}

export default function DestinationHighlights({ selectedCountry }: DestinationHighlightsProps) {
  const getHighlights = () => {
    switch (selectedCountry) {
      case 'saudi-arabia':
        return destinationHighlights.saudiArabia;
      case 'turkey':
        return destinationHighlights.turkey;
      case 'uae':
        return destinationHighlights.uae;
      case 'morocco':
        return destinationHighlights.morocco;
      case 'egypt':
        return destinationHighlights.egypt;
      case 'jordan':
        return destinationHighlights.jordan;
      case 'malaysia':
        return destinationHighlights.malaysia;
      case 'indonesia':
        return destinationHighlights.indonesia;
      case 'maldives':
        return destinationHighlights.maldives;
      case 'bosnia':
        return destinationHighlights.bosnia;
      default:
        return destinationHighlights.saudiArabia;
    }
  };

  const highlights = getHighlights();

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A1F2E] mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Destination Highlights
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover what makes this destination truly special
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {highlights.map((highlight, index) => (
            <HighlightCard key={index} highlight={highlight} />
          ))}
        </div>
      </div>
    </section>
  );
}
